import { Audio, AudioLoader, Euler, Vector3 } from 'three/webgpu'
import { types } from '@theatre/core'
import { easing } from 'maath'
import DeviceControl from './DeviceControl'
import staticPath from '../utils/staticPath'

export default class DeviceButton extends DeviceControl {
	static defaults = {
		sheet: null,
		listener: null,
		click: staticPath('audio/button_click.aac'),
		volume: 0.5,
		axis: 'x',
		distance: -0.09,
		smoothing: 0.06,
		nudge: { nudgeMultiplier: 0.001 },
		angle: { range: [-Math.PI, Math.PI] },
	}

	constructor(options = {}) {
		super(options)

		this.offset = new Vector3()
		this.spin = new Euler()
		this.pressed = 0
		this.push = 0

		this.#bindSheet()
		this.#sound()
	}

	#sound() {
		const { listener, click, volume } = this.options

		if (!listener) return

		this.sound = new Audio(listener)
		this.sound.setVolume(volume)

		new AudioLoader().load(click, buffer => this.sound.setBuffer(buffer))
	}

	#bindSheet() {
		const { sheet, nudge, angle } = this.options

		if (!sheet) return

		this.control = sheet.object('Push', {
			offset: types.compound({
				x: types.number(0, nudge),
				y: types.number(0, nudge),
				z: types.number(0, nudge),
			}),
			rotation: types.compound({
				x: types.number(0, angle),
				y: types.number(0, angle),
				z: types.number(0, angle),
			}),
			scale: types.number(1, { range: [0.5, 1.5] }),
		})

		this.unbind = this.control.onValuesChange(({ offset, rotation, scale }) => {
			this.offset.set(offset.x, offset.y, offset.z)
			this.spin.set(rotation.x, rotation.y, rotation.z)
			this.scale.copy(this.sizing).multiplyScalar(scale)
		})
	}

	press() {
		this.pressed = 1

		if (this.sound?.buffer) {
			this.sound.context.resume()
			this.sound.stop().play()
		}

		const sequence = this.options.sheet?.sequence

		if (!sequence) return

		sequence.position = 0
		sequence.play({ iterationCount: 1 })
	}

	release() {
		this.pressed = 0
	}

	update(delta) {
		easing.damp(this, 'push', this.pressed, this.options.smoothing, delta)

		this.position.copy(this.rest).add(this.offset)
		this.position[this.options.axis] += this.push * this.options.distance
		this.rotation.set(this.base.x + this.spin.x, this.base.y + this.spin.y, this.base.z + this.spin.z)
	}

	dispose() {
		this.sound?.stop()
		this.unbind?.()
		this.options.sheet?.sequence.pause()
		super.dispose()
	}
}
